import { useEffect, useState } from "react";

import { state } from "../state/workspaceStore.js";

import { clone, id, collectionName, save } from "../utils/domain/model.js";

import { notFound } from "./NotFoundPage.jsx";

import { head } from "../components/common/PageHeader.jsx";

import {
  btn,
  field,
  select,
  area,
  errorSlot,
  check,
} from "../components/forms/FormControls.jsx";

import { nodes } from "../components/common/reactHelpers.jsx";

import { esc } from "../utils/format.js";

import { tableViews } from "../components/tables/RecordTable.jsx";

/* =========================================================

   API

========================================================= */

const COLLECTION_API = "https://api.praachinorganics.com/api/Collections";

const PRODUCT_API = "https://api.praachinorganics.com/api/products";

/* =========================================================

   COLLECTION STATUS

========================================================= */

function statusToApi(value) {
  return String(value || "").toLowerCase() === "active" ? 1 : 2;
}

function statusFromApi(
  status,

  statusName,
) {
  if (String(statusName || "").toLowerCase() === "active") {
    return true;
  }

  if (String(statusName || "").toLowerCase() === "inactive") {
    return false;
  }

  return Number(status) === 1;
}

/* =========================================================

   API ERROR

========================================================= */

function getApiError(
  data,

  status,
) {
  if (data?.errors) {
    return Object.values(data.errors)

      .flat()

      .join("\n");
  }

  return (
    data?.message ||
    data?.title ||
    (typeof data === "string" ? data : "") ||
    `API request failed (${status}).`
  );
}

/* =========================================================

   API REQUEST

========================================================= */

async function request(
  url,

  options = {},
) {
  const response = await fetch(
    url,

    {
      ...options,

      headers: {
        Accept: "application/json",

        ...(options.body
          ? {
            "Content-Type": "application/json",
          }
          : {}),

        ...(options.headers || {}),
      },
    },
  );

  const contentType = response.headers.get("content-type") || "";

  let data = null;

  if (response.status !== 204) {
    try {
      data = contentType.includes("application/json")
        ? await response.json()
        : await response.text();
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    throw new Error(
      getApiError(
        data,

        response.status,
      ),
    );
  }

  return data;
}

/* =========================================================

   API COLLECTION -> EXISTING UI MODEL

========================================================= */

function mapApiCollection(
  item,

  existing = {},
) {
  const hasApiProducts =
    Array.isArray(item?.products) || Array.isArray(item?.Products);

  const apiProducts = hasApiProducts
    ? (item?.products ?? item?.Products ?? [])
    : [];

  return {
    ...existing,

    id: String(item?.id ?? item?.Id ?? existing?.id ?? ""),

    name: item?.name ?? item?.Name ?? "",

    code: item?.code ?? item?.Code ?? "",

    description: item?.description ?? item?.Description ?? "",

    order: Number(item?.displayOrder ?? item?.DisplayOrder ?? 1),

    launch: statusFromApi(
      item?.status ?? item?.Status,

      item?.statusName ?? item?.StatusName,
    ),

    archived: false,

    image: existing?.image || "",

    destination: existing?.destination || "",

    selectedProductIds: hasApiProducts
      ? apiProducts

        .map((p) => String(p?.productId ?? p?.ProductId ?? ""))

        .filter(Boolean)
      : existing?.selectedProductIds || [],

    products: apiProducts.map((p) => ({
      id: String(p?.productId ?? p?.ProductId ?? ""),

      name: p?.productName ?? p?.ProductName ?? "",

      collectionName: p?.collectionName ?? p?.CollectionName ?? "",

      isAssigned: Boolean(p?.isAssigned ?? p?.IsAssigned ?? true),
    })),

    productCount: Number(
      item?.productCount ?? item?.ProductCount ?? apiProducts.length,
    ),

    orderCount: Number(item?.orderCount ?? item?.OrderCount ?? 0),
  };
}

/* =========================================================

   API PRODUCT -> EXISTING UI MODEL

========================================================= */

function mapProductForAssignment(
  item,

  collections,

  currentCollectionId = null,
) {
  const productId =
    item?.productId ?? item?.ProductId ?? item?.id ?? item?.Id ?? "";

  const productName =
    item?.productName ?? item?.ProductName ?? item?.name ?? item?.Name ?? "";

  const collectionNameValue =
    item?.collectionName ?? item?.CollectionName ?? "";

  const isAssigned = Boolean(item?.isAssigned ?? item?.IsAssigned ?? false);

  const existing =
    state.db.products.find((p) => String(p.id) === String(productId)) || {};

  const matchedCollection = collections.find(
    (c) =>
      String(c.name || "")
        .trim()

        .toLowerCase() ===
      String(collectionNameValue || "")
        .trim()

        .toLowerCase(),
  );

  const explicitCollectionId = item?.collectionId ?? item?.CollectionId ?? null;

  const resolvedCollectionId =
    isAssigned && currentCollectionId != null
      ? currentCollectionId
      : explicitCollectionId != null
        ? explicitCollectionId
        : (matchedCollection?.id ?? existing?.collection ?? "");

  return {
    ...existing,

    id: String(productId),

    name: productName || existing?.name || "",

    collection: String(resolvedCollectionId ?? ""),

    archived: false,
  };
}

/* =========================================================

   FORM -> API PAYLOAD

========================================================= */

function buildCollectionPayload(form) {
  const formData = new FormData(form);

  const name = String(formData.get("name") || "").trim();

  const code = String(formData.get("code") || "").trim();

  const description = String(formData.get("description") || "").trim();

  const displayOrder = Number(formData.get("order"));

  const status = String(formData.get("launch") || "active");

  if (!name) {
    throw new Error("Collection name is required.");
  }

  if (!code) {
    throw new Error("Collection code is required.");
  }

  if (!Number.isInteger(displayOrder) || displayOrder <= 0) {
    throw new Error("Display order must be greater than zero.");
  }

  return {
    name,

    code,

    displayOrder,

    status: statusToApi(status),

    description: description || null,
  };
}

/* =========================================================

   COLLECTION FORM UI

========================================================= */

export function collectionForm(
  cid,

  onSubmit,

  saving = false,

  onSaveAssignment,

  assignmentSaving = false,
) {
  if (!state.collectionDraft) {
    state.collectionDraft = cid
      ? clone(state.db.collections.find((c) => String(c.id) === String(cid)))
      : {
        id: id("col"),

        name: "",

        code: "",

        description: "",

        order: state.db.collections.length + 1,

        launch: true,

        archived: false,

        image: "",

        selectedProductIds: [],
      };
  }

  let c = state.collectionDraft;

  if (!c) {
    return notFound();
  }

  const saved = state.db.collections.find((x) => String(x.id) === String(c.id));

  cid = saved?.id || cid || null;

  const assignedCount = Array.isArray(c.selectedProductIds)
    ? c.selectedProductIds.length
    : 0;

  return (
    <>
      {head(
        "COLLECTIONS",

        cid ? "Edit collection" : "Add New Collection",

        "Identifiers remain stable when you edit a display name.",

        btn(
          "Cancel",

          `data-nav="${cid ? "collection" : "collections"}" ${cid ? `data-id="${cid}"` : ""
          }`,

          true,
        ),
      )}

      <form
        id="collection-form"
        className="grid-two"
        onSubmit={onSubmit}
        onClick={(event) => {
          const button = event.target.closest(
            '[data-action="save-assignment"]',
          );

          if (!button) {
            return;
          }

          event.preventDefault();

          event.stopPropagation();

          if (!assignmentSaving && onSaveAssignment) {
            onSaveAssignment(event.currentTarget);
          }
        }}
      >
        <section className="card">
          <h2>Collection settings</h2>

          <div className="form-grid">
            {field(
              "Name *",

              "name",

              c.name,

              "text",

              "required",
            )}

            {field(
              "Unique code *",

              "code",

              c.code,

              "text",

              "required",
            )}

            {field(
              "Display order *",

              "order",

              c.order,

              "number",

              'min="1" step="1" required',
            )}

            {select(
              "Status",

              "launch",

              c.launch ? "active" : "inactive",

              ["active", "inactive"],
            )}
          </div>

          {area(
            "Description *",

            "description",

            c.description,
          )}

          <button type="submit" className="btn" disabled={saving}>
            {saving ? "Saving..." : "Save collection"}
          </button>

          {errorSlot()}
        </section>

        <section className="card">
          <h2>Product assignment</h2>

          <p className="hint">
            {`Assigned to this collection: ${assignedCount} product${assignedCount === 1 ? "" : "s"
              }`}
          </p>

          <p className="hint">
            {
              "Select products to assign to this collection. Products marked Assigned here already belong to this collection. A product from another collection will be moved here when you save."
            }
          </p>

          {/* {select(

            "Destination for removed members",

            "destination",

            c.destination ||

              "",



            [

              [

                "",

                "Choose destination…",

              ],



              ...state.db.collections

                .filter(

                  (x) =>

                    !x.archived,

                )

                .map(

                  (x) => [

                    x.id,

                    x.name,

                  ],

                ),

            ],

          )} */}

          <div className="member-list">
            {nodes(
              state.db.products

                .filter((p) => !p.archived)

                .map((p) => {
                  const selectedHere = Array.isArray(c.selectedProductIds)
                    ? c.selectedProductIds

                      .map(String)

                      .includes(String(p.id))
                    : String(p.collection) === String(cid);

                  const currentCollectionLabel =
                    collectionName(
                      state.db,

                      p.collection,
                    ) || "Unassigned";

                  const belongsElsewhere =
                    Boolean(p.collection) &&
                    String(p.collection) !== String(cid);

                  const productStatusLabel = selectedHere
                    ? " · Assigned here"
                    : belongsElsewhere
                      ? ` · ${esc(
                        currentCollectionLabel,
                      )} · Will move here if selected`
                      : " · Unassigned";

                  return check(
                    esc(p.name) + productStatusLabel,

                    `member-${p.id}`,

                    selectedHere,
                  );
                }),
            )}
          </div>

          <div className="assignment-actions">
            {btn(
              assignmentSaving
                ? "Saving Assignment..."
                : "Save Product Assignment",

              'data-action="save-assignment"',
            )}

            <p className="hint">
              {
                "Assignments save independently. Collection-information edits remain staged until Save collection."
              }
            </p>
          </div>

          <p id="assignment-feedback" className="hint" role="status">
            {esc(c.assignmentMessage || "")}
          </p>
        </section>
      </form>
    </>
  );
}

/* =========================================================

   COLLECTION FORM PAGE

========================================================= */

export default function CollectionFormPage({ recordId } = {}) {
  const [loading, setLoading] = useState(Boolean(recordId));

  const [saving, setSaving] = useState(false);

  const [assignmentSaving, setAssignmentSaving] = useState(false);

  const [loadError, setLoadError] = useState("");

  const [, setRevision] = useState(0);

  /* =======================================================

     RESET DRAFT WHEN ROUTE CHANGES

  ======================================================= */

  useEffect(() => {
    state.collectionDraft = null;
  }, [recordId]);

  /* =======================================================

   LOAD COLLECTIONS + PRODUCTS FOR ASSIGNMENT

   GET /api/Collections

   Edit mode:
   GET /api/Collections/{id}/products

   Add mode fallback:
   GET /api/products

   The collection-products endpoint returns ALL products
   with IsAssigned for the current collection.

======================================================= */

  useEffect(() => {
    let active = true;

    async function loadAssignmentData() {
      try {
        const collectionData = await request(COLLECTION_API);

        if (!active) {
          return;
        }

        const mappedCollections = Array.isArray(collectionData)
          ? collectionData

            .map((item) => {
              const existing = state.db.collections.find(
                (collection) =>
                  String(collection.id) ===
                  String(item?.id ?? item?.Id ?? ""),
              );

              return mapApiCollection(
                item,

                existing,
              );
            })

            .filter((collection) => Boolean(collection.id))
          : [];

        state.db.collections = mappedCollections;

        const collectionId = Number(recordId);

        const hasValidCollectionId =
          Number.isInteger(collectionId) && collectionId > 0;

        const productData = await request(
          hasValidCollectionId
            ? `${COLLECTION_API}/${collectionId}/products`
            : PRODUCT_API,
        );

        if (!active) {
          return;
        }

        if (Array.isArray(productData)) {
          state.db.products = productData.map((item) =>
            mapProductForAssignment(
              item,

              mappedCollections,

              hasValidCollectionId ? collectionId : null,
            ),
          );
        }

        save(
          localStorage,

          state.db,
        );

        setRevision((n) => n + 1);
      } catch (error) {
        console.error(
          "COLLECTION ASSIGNMENT DATA LOAD ERROR:",

          error,
        );
      }
    }

    loadAssignmentData();

    return () => {
      active = false;
    };
  }, [recordId]);

  /* =======================================================

       EDIT MODE - GET COLLECTION BY ID



       GET /api/Collections/{id}



       CollectionDetailsResponse already includes Products[],

       so assigned checkboxes come from this response.

    ======================================================= */

  useEffect(() => {
    let active = true;

    async function loadCollection() {
      if (!recordId) {
        setLoading(false);

        return;
      }

      try {
        setLoading(true);

        setLoadError("");

        const collectionId = Number(recordId);

        if (!Number.isInteger(collectionId) || collectionId <= 0) {
          throw new Error("A valid collection ID is required.");
        }

        const response = await request(`${COLLECTION_API}/${collectionId}`);

        if (!active) {
          return;
        }

        const existing = state.db.collections.find(
          (item) => String(item.id) === String(collectionId),
        );

        const mappedCollection = mapApiCollection(
          response,

          existing,
        );

        const existingIndex = state.db.collections.findIndex(
          (item) => String(item.id) === String(collectionId),
        );

        if (existingIndex >= 0) {
          state.db.collections[existingIndex] = mappedCollection;
        } else {
          state.db.collections.push(mappedCollection);
        }

        state.collectionDraft = clone(mappedCollection);

        save(
          localStorage,

          state.db,
        );

        setRevision((n) => n + 1);
      } catch (error) {
        console.error(
          "COLLECTION LOAD ERROR:",

          error,
        );

        if (active) {
          setLoadError(error?.message || "Unable to load the collection.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadCollection();

    return () => {
      active = false;
    };
  }, [recordId]);

  /* =======================================================

     ADD / EDIT COLLECTION

  ======================================================= */

  async function handleSubmit(event) {
    event.preventDefault();

    event.stopPropagation();

    const errorElement = document.getElementById("form-error");

    if (errorElement) {
      errorElement.textContent = "";
    }

    try {
      setSaving(true);

      const payload = buildCollectionPayload(event.currentTarget);

      const isEdit = Boolean(recordId);

      let url = COLLECTION_API;

      let method = "POST";

      if (isEdit) {
        const collectionId = Number(recordId);

        if (!Number.isInteger(collectionId) || collectionId <= 0) {
          throw new Error("A valid collection ID is required for updating.");
        }

        url = `${COLLECTION_API}/${collectionId}`;

        method = "PUT";
      }

      const response = await request(
        url,

        {
          method,

          body: JSON.stringify(payload),
        },
      );

      const existing = state.db.collections.find(
        (item) =>
          String(item.id) ===
          String(response?.id ?? response?.Id ?? recordId ?? ""),
      );

      const mappedCollection = mapApiCollection(
        response,

        existing,
      );

      if (!mappedCollection.id) {
        throw new Error(
          "The Collection API did not return a valid collection ID.",
        );
      }

      const existingIndex = state.db.collections.findIndex(
        (item) => String(item.id) === String(mappedCollection.id),
      );

      if (existingIndex >= 0) {
        state.db.collections[existingIndex] = mappedCollection;
      } else {
        state.db.collections.push(mappedCollection);
      }

      save(
        localStorage,

        state.db,
      );

      state.collectionDraft = null;

      location.hash = "#collections";
    } catch (error) {
      console.error(
        recordId ? "COLLECTION UPDATE ERROR:" : "COLLECTION CREATE ERROR:",

        error,
      );

      if (errorElement) {
        errorElement.textContent =
          error?.message ||
          (recordId
            ? "Unable to update the collection."
            : "Unable to create the collection.");
      }
    } finally {
      setSaving(false);
    }
  }

  /* =======================================================

     SAVE PRODUCT ASSIGNMENT



     PUT /api/Collections/{id}/products



     {

       productIds: [1, 2, 3]

     }

  ======================================================= */

  async function handleSaveAssignment(form) {
    const collectionId = Number(recordId);

    if (!Number.isInteger(collectionId) || collectionId <= 0) {
      state.collectionDraft = {
        ...state.collectionDraft,

        assignmentMessage:
          "Save the collection first before assigning products.",
      };

      setRevision((n) => n + 1);

      return;
    }

    try {
      setAssignmentSaving(true);

      const selectedProductIds = Array.from(
        form.querySelectorAll('input[name^="member-"]:checked'),
      )

        .map((input) =>
          Number(
            String(input.name).replace(
              "member-",

              "",
            ),
          ),
        )

        .filter((productId) => Number.isInteger(productId) && productId > 0);

      await request(
        `${COLLECTION_API}/${collectionId}/products`,

        {
          method: "PUT",

          body: JSON.stringify({
            productIds: selectedProductIds,
          }),
        },
      );

      /*

        Re-fetch details after assignment save.

        This updates Products[] and ProductCount from backend.

      */

      const refreshed = await request(`${COLLECTION_API}/${collectionId}`);

      const refreshedProducts = await request(
        `${COLLECTION_API}/${collectionId}/products`,
      );

      const currentCollection = state.db.collections.find(
        (item) => String(item.id) === String(collectionId),
      );

      const mappedCollection = mapApiCollection(
        refreshed,

        currentCollection,
      );

      mappedCollection.assignmentMessage =
        "Product assignment saved successfully.";

      const collectionIndex = state.db.collections.findIndex(
        (item) => String(item.id) === String(collectionId),
      );

      if (collectionIndex >= 0) {
        state.db.collections[collectionIndex] = mappedCollection;
      } else {
        state.db.collections.push(mappedCollection);
      }

      /*

        Refresh product -> collection ownership from backend.This makes the old collection disappear and the newly

        assigned collection appear immediately after Save.

      */

      if (Array.isArray(refreshedProducts)) {
        state.db.products = refreshedProducts.map((item) =>
          mapProductForAssignment(
            item,

            state.db.collections,

            collectionId,
          ),
        );
      }

      state.collectionDraft = clone(mappedCollection);

      save(
        localStorage,

        state.db,
      );

      setRevision((n) => n + 1);
    } catch (error) {
      console.error(
        "PRODUCT ASSIGNMENT ERROR:",

        error,
      );

      state.collectionDraft = {
        ...state.collectionDraft,

        assignmentMessage:
          error?.message || "Unable to save product assignment.",
      };

      setRevision((n) => n + 1);
    } finally {
      setAssignmentSaving(false);
    }
  }

  /* =======================================================

     LOADING

  ======================================================= */

  if (recordId && loading) {
    return tableViews(
      <>
        {head(
          "COLLECTIONS",

          "Edit collection",

          "Loading collection details...",

          <></>,
        )}

        <section className="card">
          <p className="hint">Loading collection...</p>
        </section>
      </>,
    );
  }

  if (recordId && loadError) {
    return tableViews(
      <>
        {head(
          "COLLECTIONS",
          "Edit collection",
          "",
          btn("Back", 'data-nav="collections"', true),
        )}

        <section className="card">
          <p className="error" role="alert">
            {loadError}
          </p>
        </section>
      </>,
    );
  }

  return tableViews(
    collectionForm(
      recordId,
      handleSubmit,
      saving,
      handleSaveAssignment,
      assignmentSaving,
    ),
  );
}
